import { useState } from 'react';
import { useGuild } from '../contexts/GuildContext';
import { useToast } from '../contexts/ToastContext';
import { useRules, useDeleteRule } from '../hooks/useRules';
import { getErrorMessage } from '../api/errorHandler';
import DashboardLayout from '../components/DashboardLayout';
import ServerSelector from '../components/ServerSelector';
import RuleModal from '../components/RuleModal';
import type { Rule } from '../types';

export default function RulesPage() {
  const { selectedGuildId, setSelectedGuildId } = useGuild();
  const { showToast } = useToast();
  const { data: rules, isLoading, error } = useRules(selectedGuildId);
  const deleteRule = useDeleteRule(selectedGuildId);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);

  const handleCreateRule = () => {
    setEditingRule(null);
    setIsModalOpen(true);
  };

  const handleEditRule = (rule: Rule) => {
    setEditingRule(rule);
    setIsModalOpen(true);
  };

  const handleDeleteRule = async (ruleName: string) => {
    if (confirm(`"${ruleName}" 규칙을 삭제하시겠습니까?`)) {
      try {
        await deleteRule.mutateAsync(ruleName);
        showToast('success', '규칙이 삭제되었습니다');
      } catch (error) {
        showToast('error', getErrorMessage(error));
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRule(null);
  };

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">규칙 관리</h1>
              <p className="text-gray-600">서버 규칙과 자동 제재 단계를 설정하세요</p>
            </div>
            <div className="mt-4 md:mt-0 md:w-80">
              <ServerSelector
                selectedGuildId={selectedGuildId}
                onSelectGuild={setSelectedGuildId}
              />
            </div>
          </div>

          {!selectedGuildId ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-700">서버를 선택해주세요</p>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-700">규칙을 불러올 수 없습니다</p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <button
                  onClick={handleCreateRule}
                  className="px-4 py-2 bg-gradient-to-r from-[#FF7A7A] to-[#DD8DFF] text-white rounded-lg hover:opacity-90 transition-opacity"
                >
                  + 새 규칙 추가
                </button>
              </div>

              {rules && rules.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                  <p className="text-gray-600">등록된 규칙이 없습니다</p>
                  <button
                    onClick={handleCreateRule}
                    className="mt-4 text-sm text-blue-600 hover:text-blue-700"
                  >
                    첫 번째 규칙 만들기
                  </button>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          규칙 이름
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          내용
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          제재 단계
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          작업
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {rules?.map((rule) => (
                        <tr key={rule._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{rule.name}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-600 max-w-md truncate">
                              {rule.content}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600">
                              {rule.punishments.length}단계
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleEditRule(rule)}
                              className="text-blue-600 hover:text-blue-900 mr-4"
                            >
                              수정
                            </button>
                            <button
                              onClick={() => handleDeleteRule(rule.name)}
                              className="text-red-600 hover:text-red-900"
                            >
                              삭제
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isModalOpen && (
        <RuleModal
          guildId={selectedGuildId}
          rule={editingRule}
          onClose={handleCloseModal}
        />
      )}
    </DashboardLayout>
  );
}
